"use client";
import { IconArrowNarrowRight } from "@tabler/icons-react";
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from "react";

// Modal Component
const Modal = ({ slide, onClose }) => {
  if (!slide) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-11/12 p-6 bg-white rounded-lg shadow-xl md:w-3/5 lg:w-2/5">
        <button
          className="absolute text-2xl font-bold top-2 right-2"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
        <img
          src={slide?.photo}
          alt={slide?.title}
          className="object-cover w-full h-56 mb-4 rounded-lg"
        />
        <h2 className="mb-2 text-2xl font-semibold">{slide?.title}</h2>
        <div
          className="mb-4 dynamic-content"
          dangerouslySetInnerHTML={{ __html: slide?.description }}
        />
        {slide?.company_logo && (
          <img
            src={slide?.company_logo}
            alt="Company Logo"
            className="object-cover h-16 mb-2"
          />
        )}
        <p className="text-gray-700">
          <strong>Service Type:</strong>{" "}
          {slide?.service_type === "RE"
            ? "Research & Evaluation"
            : "Organization & Institutional Development"}
        </p>
        <p className="text-gray-700">
          <strong>Sub Service Area:</strong> {slide?.sub_service_area.name}
        </p>
        <p className="text-gray-700">
          <strong>Sectoral Presence:</strong> {slide?.practice_area.name}
        </p>
      </div>
    </div>
  );
};

Modal.propTypes = {
  slide: PropTypes.shape({
    photo: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    company_logo: PropTypes.string,
    service_type: PropTypes.string,
    sub_service_area: PropTypes.shape({
      name: PropTypes.string
    }),
    practice_area: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  onClose: PropTypes.func.isRequired
};

// Slide Component
const Slide = ({ slide, index, current, openModal }) => {
  const slideRef = useRef(null);
  const xRef = useRef(0);
  const yRef = useRef(0);
  const frameRef = useRef();

  useEffect(() => {
    const animate = () => {
      if (slideRef.current) {
        slideRef.current.style.setProperty("--x", `${xRef.current}px`);
        slideRef.current.style.setProperty("--y", `${yRef.current}px`);
      }
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  const handleMouseMove = (e) => {
    const el = slideRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    xRef.current = e.clientX - (rect.left + rect.width / 1);
    yRef.current = e.clientY - (rect.top + rect.height / 1);
  };

  return (
    <div className="[perspective:1200px] [transform-style:preserve-3d] flex-shrink-0 w-[90vw] h-[90vw] sm:w-[70vw] sm:h-[70vw] md:w-[50vw] md:h-[50vw] lg:w-[35vmin] lg:h-[35vmin] mx-[1vw] sm:mx-[1.5vw] md:mx-[2vmin]">
      <li
        ref={slideRef}
        className="relative flex flex-col items-center justify-center w-full h-full text-center text-white"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          xRef.current = 0;
          yRef.current = 0;
        }}
        style={{
          transform:
            current !== index
              ? "scale(0.98) rotateX(8deg)"
              : "scale(1) rotateX(0deg)",
          transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          transformOrigin: "bottom",
        }}
      >
        <div
          className="absolute top-0 left-0 w-full h-full overflow-hidden bg-[#1D1F2F] rounded-[1%]"
          style={{
            transform:
              current === index
                ? "translate3d(calc(var(--x) / 30), calc(var(--y) / 30), 0)"
                : "none",
          }}
        >
          <img
            src={slide?.photo}
            alt={slide?.title}
            className="absolute inset-0 object-cover w-[120%] h-[120%]"
            style={{ opacity: current === index ? 1 : 0.5 }}
            loading="eager"
            decoding="async"
          />
          {current === index && (
            <div className="absolute inset-0 bg-black/30" />
          )}
        </div>
        <article className="relative p-[4vmin]">
          <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold">
            {slide?.title?.length > 30
              ? `${slide?.title.substring(0, 40)}...`
              : slide?.title}
          </h2>
          <div className="flex justify-center">
            <button
              className="h-8 sm:h-10 md:h-12 px-3 sm:px-4 py-1 sm:py-2 mt-4 sm:mt-6 text-sm sm:text-base text-black bg-white border rounded-2xl hover:shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                openModal(slide);
              }}
            >
              View Details
            </button>
          </div>
        </article>
      </li>
    </div>
  );
};

Slide.propTypes = {
  slide: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    photo: PropTypes.string,
    title: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  openModal: PropTypes.func.isRequired
};

// Carousel Control Button
const CarouselControl = ({ direction, handleClick }) => (
  <button
    className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/90 hover:bg-blue-600 shadow-lg hover:scale-110 active:scale-95 transition-all"
    onClick={handleClick}
    aria-label={`${direction} slide`}
  >
    <IconArrowNarrowRight 
      className={`text-white w-4 h-4 ${direction === 'Previous' ? 'rotate-180' : ''}`} 
    />
  </button>
);

CarouselControl.propTypes = {
  direction: PropTypes.oneOf(['Previous', 'Next']).isRequired,
  handleClick: PropTypes.func.isRequired
};

// Progress Bar Component
const ProgressBar = ({ current, total }) => {
  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4">
      <CarouselControl direction="Previous" handleClick={() => {}} />
      <div className="flex items-center gap-2">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className={`h-2 rounded-full transition-all duration-300 ${
              current === i + 1
                ? 'w-8 bg-blue-500'
                : 'w-2 bg-gray-300'
            }`}
          />
        ))}
      </div>
      <CarouselControl direction="Next" handleClick={() => {}} />
    </div>
  );
};

ProgressBar.propTypes = {
  current: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired
};

// Main Carousel Component
export function Carousel({ slides }) {
  const [current, setCurrent] = useState(1);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [transitioning, setTransitioning] = useState(true);

  // Create extended slides with unique keys
  const extendedSlides = [
    { ...slides[slides.length - 1], uniqueKey: 'clone-start' },
    ...slides.map((slide, index) => ({ ...slide, uniqueKey: `slide-${index}` })),
    { ...slides[0], uniqueKey: 'clone-end' }
  ];

  const handleNextClick = () => {
    if (current >= slides.length) {
      setTransitioning(false);
      setCurrent(1);
      setTimeout(() => {
        setTransitioning(true);
      }, 50);
    } else {
      setCurrent((prev) => prev + 1);
    }
  };

  const handlePreviousClick = () => {
    if (current <= 1) {
      setTransitioning(false);
      setCurrent(slides.length);
      setTimeout(() => {
        setTransitioning(true);
      }, 50);
    } else {
      setCurrent((prev) => prev - 1);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleNextClick();
    }, 3000);

    return () => clearInterval(interval);
  }, [current]);

  const handleTransitionEnd = () => {
    if (current === 0) {
      setTransitioning(false);
      setCurrent(slides.length);
      setTimeout(() => {
        setTransitioning(true);
      }, 50);
    } else if (current === slides.length + 1) {
      setTransitioning(false);
      setCurrent(1);
      setTimeout(() => {
        setTransitioning(true);
      }, 50);
    }
  };

  return (
    <div className="relative w-full mb-16 sm:mb-20 md:mb-24">
      <div className="flex justify-center">
        <ul
          className={`flex transition-transform duration-500 ease-in-out`}
          style={{
            transform: `translateX(calc(-${current * (window.innerWidth < 640 ? 92 : window.innerWidth < 768 ? 72 : window.innerWidth < 1024 ? 52 : 39)}vmin + 50% - ${window.innerWidth < 640 ? 46 : window.innerWidth < 768 ? 36 : window.innerWidth < 1024 ? 26 : 19.5}vmin))`,
            transition: transitioning ? "transform 0.5s ease-in-out" : "none",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {extendedSlides.map((slide, index) => (
            <Slide
              key={slide.uniqueKey}
              slide={slide}
              index={index}
              current={current}
              openModal={setSelectedSlide}
            />
          ))}
        </ul>
      </div>
      
      {/* Navigation and Progress Bar */}
      <div className="absolute -bottom-8 sm:-bottom-10 md:-bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-2 sm:gap-3 md:gap-4">
        <CarouselControl direction="Previous" handleClick={handlePreviousClick} />
        <div className="flex items-center gap-1 sm:gap-2">
          {Array.from({ length: slides.length }, (_, i) => (
            <div
              key={i}
              className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                current === i + 1
                  ? 'w-6 sm:w-8 bg-blue-500'
                  : 'w-1.5 sm:w-2 bg-gray-500'
              }`}
            />
          ))}
        </div>
        <CarouselControl direction="Next" handleClick={handleNextClick} />
      </div>
      <Modal slide={selectedSlide} onClose={() => setSelectedSlide(null)} />
    </div>
  );
}

Carousel.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      photo: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      company_logo: PropTypes.string,
      service_type: PropTypes.string,
      sub_service_area: PropTypes.shape({
        name: PropTypes.string
      }),
      practice_area: PropTypes.shape({
        name: PropTypes.string
      })
    })
  ).isRequired
};
